import {cyan, gray} from 'chalk';
import {dedent} from 'ts-dedent';

import {option, toArray} from '~/core/config';

const auth = option({
    flags: '--auth <value>',
    desc: `
        Authorization token for the AI provider.
        Accepts the raw token value or a path to a file containing the token.

        Yandex AI Studio: IAM token (Bearer) or service-account API key.
        OpenAI / OpenRouter: Bearer key (sk-...).
        Anthropic: x-api-key (sk-ant-...).

        Env fallback: YANDEX_API_KEY / YC_IAM_TOKEN, OPENAI_API_KEY, OPENROUTER_API_KEY, ANTHROPIC_API_KEY.

        Optional when the provider auth header (Authorization or x-api-key)
        is already supplied via ${cyan('--api-header')} / ${cyan('apiHeaders')}.
    `,
});

const folder = option({
    flags: '--folder <value>',
    desc: `
        Yandex AI Studio folder ID. Required when --model is a short model name
        (e.g. "yandexgpt-lite") so the full gpt:// URI can be built.
    `,
});

const model = option({
    flags: '--model <value>',
    desc: `
        Target model identifier.

        Yandex AI Studio: short name ("yandexgpt-lite", "yandexgpt") or full URI ("gpt://<folder>/yandexgpt/latest").
        OpenAI: e.g. "gpt-4o-mini".
        OpenRouter: e.g. "anthropic/claude-3.5-sonnet".
        Anthropic: e.g. "claude-sonnet-4-5".
    `,
});

const fallbackModel = option({
    flags: '--fallback-model <value>',
    desc: `
        Fallback model identifier in the same format as --model.

        When a request keeps failing after all retries, it is retried with
        this model on the same provider, credentials and API settings.
        Auth errors are never retried. Disabled by default.
    `,
});

const fallbackApiBase = option({
    flags: '--fallback-api-base <url>',
    desc: `
        Override the API base URL for the fallback model only.

        Useful with gateways which route by URL path: there a reserve model of
        another vendor is unreachable through the primary base URL.

        Requires ${cyan('--fallback-model')}. The fallback keeps the provider,
        credentials and headers of the primary model. Empty by default: the
        fallback reuses ${cyan('--api-base')}.
    `,
});

const apiBase = option({
    flags: '--api-base <url>',
    desc: `
        Override the API base URL. Useful for self-hosted and internal
        installations of compatible APIs.

        The provider request path is appended automatically:
        yandexgpt: <base>/foundationModels/v1/completion
        openai / openrouter: <base>/chat/completions (include /v1 into the base)
        anthropic: <base>/messages (include /v1 into the base)

        Env fallback: OPENAI_BASE_URL, OPENROUTER_BASE_URL, ANTHROPIC_BASE_URL.
    `,
});

const apiHeader = option({
    flags: '--api-header <header>',
    desc: `
        Additional HTTP header for LLM API requests in "Name: value" format.
        Repeat the option to pass several headers.
        Useful for internal gateways which require extra auth headers.

        Config alternative: ${cyan('apiHeaders')} object in the yfm config.
    `,
    parser: toArray,
});

const systemPrompt = option({
    flags: '--system-prompt <value>',
    desc: `
        System prompt for the LLM. Accepts a string or a path to a file.

        Supports placeholders: {{source}}, {{target}}, {{glossary}}, {{context}}, {{separator}}, {{fragments}}.

        By default the user prompt is appended to the built-in technical-translator system prompt.
        Use --prompt-mode replace to fully replace the default.
    `,
});

const userPrompt = option({
    flags: '--user-prompt <value>',
    desc: `
        User prompt template. Accepts a string or a path to a file.
        Supports placeholders: {{source}}, {{target}}, {{glossary}}, {{context}}, {{separator}}, {{fragments}}, {{text}}.
    `,
});

const promptMode = option({
    flags: '--prompt-mode <mode>',
    desc: `
        How the user-supplied system prompt interacts with the default one.

        ${cyan('append')} - supplied system prompt is appended to the built-in default.
        ${cyan('replace')} - supplied system prompt fully replaces the built-in default.
    `,
    choices: ['append', 'replace'],
    defaultInfo: 'append',
});

const judge = option({
    flags: '--judge',
    desc: `
        Score translated units with a judge model after translation and
        write a quality report to the output directory. Opt-in: roughly
        doubles token usage. Segments below --judge-threshold are also
        reported as warnings.
    `,
});

const judgeModel = option({
    flags: '--judge-model <name>',
    desc: 'Model used for quality scoring. Defaults to the translation model.',
});

const judgeThreshold = option({
    flags: '--judge-threshold <num>',
    desc: 'Score below which a segment is reported as low quality.',
    parser: (value: string) => Number.parseInt(value, 10),
    defaultInfo: 70,
});

const cacheDir = option({
    flags: '--cache-dir <path>',
    desc: `
        Enable a persistent translation cache in the given directory.
        Repeated runs reuse stored translations and send only new or changed
        units to the LLM. Each model keeps its own cache file; the cache is
        reset when prompts or glossary change.
    `,
});

const noCache = option({
    flags: '--no-cache',
    desc: 'Disable the persistent translation cache for this run.',
});

const noMemoryHints = option({
    flags: '--no-memory-hints',
    desc: `
        Do not send a changed unit together with its previous version from the
        seed memory (see yfm translate seed). By default a unit whose earlier
        wording the seed knows is sent with that wording, its existing
        translation and the changes, so the model applies the edit instead of
        translating from scratch. Config alternative: ${cyan('memoryHints: false')}.
    `,
});

const contextFile = option({
    flags: '--context-file <value>',
    desc: `
        Additional context for the translation prompt: project info, style guides,
        UI text lists, terminology notes and other reference materials of arbitrary
        structure. Repeat the option to pass several sections.

        Accepts a path to a text file (md, json, txt - the content is passed
        to the model as-is) or a literal multi-line text block.

        Sections are appended to the system prompt. Add the {{contextFiles}}
        placeholder to --system-prompt or --user-prompt to control placement.

        Config alternative: ${cyan('contextFiles')} list in the yfm config.
    `,
    parser: toArray,
});

const glossaryExample = gray(dedent`
    glossaryPairs:
      - sourceText: string
        translatedText: string
`);

const glossary = option({
    flags: '--glossary <path>',
    desc: `
        Path to a YAML file with required term translations.

        Config example:
        ${glossaryExample}
    `,
});

export const NO_TEMPERATURE = 'none';

const temperature = option({
    flags: '--temperature <num>',
    desc: `
        Sampling temperature. Defaults to 0 for deterministic translation:
        the same source produces the same translation on every run.

        Pass ${cyan('none')} to leave the parameter out of the request and let
        the model use its own default. Newer models accept nothing but their
        default and are recovered automatically, so this is only needed to opt
        out deliberately.
    `,
    parser: (value: string) => (value === NO_TEMPERATURE ? NO_TEMPERATURE : Number(value)),
    defaultInfo: 0,
});

const maxOutputTokens = option({
    flags: '--max-output-tokens <num>',
    desc: 'Maximum tokens in a single LLM response.',
    parser: (value: string) => Number.parseInt(value, 10),
    defaultInfo: 4000,
});

const maxBatchTokens = option({
    flags: '--max-batch-tokens <num>',
    desc: `
        Token budget for a single LLM request. Translation units are batched up to this
        limit and sent together. Smaller values are safer but slower.
    `,
    parser: (value: string) => Number.parseInt(value, 10),
    defaultInfo: 2000,
});

const maxConcurrency = option({
    flags: '--max-concurrency <num>',
    desc: 'Maximum concurrent LLM requests.',
    parser: (value: string) => Number.parseInt(value, 10),
    defaultInfo: 5,
});

const retry = option({
    flags: '--retry <num>',
    desc: 'Number of retries on retryable LLM errors.',
    parser: (value: string) => Number.parseInt(value, 10),
    defaultInfo: 3,
});

const rateLimitRetry = option({
    flags: '--rate-limit-retry <num>',
    desc: `
        Number of retries for rate limited (429) requests. Rate limits are
        transient by definition, so they get a bigger budget than generic
        retryable errors: waits grow exponentially up to 60 seconds per
        attempt and honor the server-provided Retry-After header. While a
        rate limit window lasts, all pending requests are paused together.
    `,
    parser: (value: string) => Number.parseInt(value, 10),
    defaultInfo: 8,
});

export const options = {
    auth,
    folder,
    model,
    fallbackModel,
    apiBase,
    fallbackApiBase,
    apiHeader,
    systemPrompt,
    userPrompt,
    promptMode,
    contextFile,
    glossary,
    judge,
    judgeModel,
    judgeThreshold,
    cacheDir,
    noCache,
    noMemoryHints,
    temperature,
    maxOutputTokens,
    maxBatchTokens,
    maxConcurrency,
    retry,
    rateLimitRetry,
};
